import { Link } from 'react-router-dom';
import { useEffect, useRef } from 'react';

export default function OurStory() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const o = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) ref.current?.classList.add('visible');
    }, { threshold: 0.1 });
    if (ref.current) o.observe(ref.current);
    return () => o.disconnect();
  }, []);

  return (
    <section className="py-28 bg-white overflow-hidden">
      <div ref={ref} className="fade-up max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">

          {/* Left — staggered image stack */}
          <div className="lg:col-span-6 relative">
            {/* Main image */}
            <div className="relative rounded-2xl overflow-hidden h-[560px] ml-0 mr-8">
              <img
                src="https://public.readdy.ai/ai/img_res/4e8503eeff9c919fb01ee19c520cca5c.jpg"
                alt="Eclectic Home furniture showroom Las Vegas"
                title="About Eclectic Home - Furniture Store Las Vegas"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-dark/30 to-transparent" />
            </div>

            {/* Floating stats card */}
            <div className="absolute -bottom-4 -right-2 bg-gold text-white p-7 rounded-2xl w-52">
              <div className="font-serif text-5xl font-bold leading-none mb-1">20+</div>
              <div className="text-white/75 text-xs uppercase tracking-wider leading-relaxed mt-2">Years Furnishing Las Vegas Homes</div>
            </div>

            {/* Inset secondary image */}
            <div className="absolute top-8 -right-2 w-44 h-44 rounded-xl overflow-hidden border-4 border-white">
              <img
                src="https://readdy.ai/api/search-image?query=close%20up%20craftsman%20hand-finishing%20premium%20solid%20wood%20furniture%20natural%20grain%20surface%20artisan%20quality%20warm%20studio%20lighting%20craftsmanship%20detail&width=300&height=300&seq=story-2026-detail&orientation=squarish"
                alt="Handcrafted high-quality furniture detail"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Right — content */}
          <div className="lg:col-span-6 lg:pl-6">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-8 h-px bg-gold" />
              <span className="text-gold text-xs font-bold uppercase tracking-[0.25em]">Our Story</span>
            </div>
            <h2 className="font-serif text-5xl font-bold text-dark leading-tight mb-8">
              Built on Taste.
              <br />
              <span className="text-gold">Driven</span> by Craft.
            </h2>

            <div className="space-y-4 text-dark/60 text-sm leading-relaxed mb-8">
              <p>
                Eclectic Home was born from a simple frustration: beautiful, quality furniture was 
                either out of reach or impossible to find in one place. So in 2004, we opened our 
                first Las Vegas showroom with a curated selection of pieces that respected both 
                craft and livability.
              </p>
              <p>
                Twenty years later, we carry over 500 handpicked pieces from independent craftsmen 
                and design-forward manufacturers worldwide. Every item is selected through the same lens — 
                does it look beautiful, is it built to last, and does it justify its place in someone&apos;s home?
              </p>
              <p>
                Our team includes certified interior designers who offer complimentary consultations 
                whether you&apos;re furnishing a single room or starting from scratch.
              </p>
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-2 gap-4 mb-10">
              {[
                { val: '500+', label: 'Curated Pieces' },
                { val: '5,000+', label: 'Happy Clients' },
                { val: 'Free', label: 'Design Consult' },
                { val: '10yr', label: 'Frame Warranty' },
              ].map((s) => (
                <div key={s.label} className="bg-cream-mid rounded-xl p-5 border border-cream-dark">
                  <div className="font-serif text-3xl font-bold text-dark mb-1">{s.val}</div>
                  <div className="text-dark/45 text-xs uppercase tracking-wider">{s.label}</div>
                </div>
              ))}
            </div>

            <Link
              to="/about"
              className="inline-flex items-center gap-3 bg-dark text-white px-8 py-4 rounded-lg text-sm font-bold uppercase tracking-widest hover:bg-gold transition-colors cursor-pointer whitespace-nowrap"
            >
              Our Full Story
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line" />
              </div>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
